import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiBatch, unwrapBatch } from '@/api/batch';
import type { AccountProfileResponse, PasskeysResponse } from '@/api/types';

export interface BatchAccountProfileData {
    profile: AccountProfileResponse;
    passkeys: PasskeysResponse;
}

/**
 * Hook to fetch account profile and passkeys in a single batch request.
 * This minimizes API calls and Lambda invocations on the ProfilePage.
 */
export function useBatchAccountProfile() {
    return useQuery({
        queryKey: ['batch-account-profile'],
        queryFn: async (): Promise<BatchAccountProfileData> => {
            const result = await apiBatch<{
                profile: AccountProfileResponse;
                passkeys: PasskeysResponse;
            }>([
                { id: 'profile', path: '/api/account/profile' },
                { id: 'passkeys', path: '/api/account/passkeys' },
            ]);

            return {
                profile: unwrapBatch(result.responses.profile, 'profile'),
                passkeys: unwrapBatch(result.responses.passkeys, 'passkeys'),
            };
        },
    });
}

/**
 * Returns a function to refresh the batch account profile data.
 * Call this after mutations (email change, passkey add/delete) to update the UI.
 */
export function useRefreshBatchAccountProfile() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['batch-account-profile'] });
}
